// Ausgehende Webhooks: Ereignisse per signiertem POST an externe https-Ziele.
import { FIXED_LIMITS, type ChannelDto, type OutgoingWebhookDeliveryDto, type OutgoingWebhookDto, type OutgoingWebhookEvent } from '@lagerfeuer/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { outgoingWebhooksApi } from '../api';
import { formatDateTime } from '../format';
import { toast } from '../toast';
import { Badge, Button, ConfirmButton, Dialog, Empty, Field, Loading, SecretReveal } from '../ui';
import {
  ALL_WEBHOOK_EVENTS,
  channelLabel,
  DELIVERY_PAGE,
  deliveriesKey,
  outgoingKey,
  SIGNATURE_EXAMPLE,
  validateHttpsUrl,
  WEBHOOK_EVENT_LABELS,
  webhooksErrorText,
} from './webhooks-utils';

type FormBody = { name: string; url: string; events: OutgoingWebhookEvent[]; channelId?: string | null };

function SecretDialog(props: { webhook: OutgoingWebhookDto; title: string; onClose: () => void }) {
  return (
    <Dialog
      title={props.title}
      testId="webhooks-out-secret-dialog"
      onClose={props.onClose}
      footer={
        <Button variant="primary" testId="webhooks-out-secret-done" onClick={props.onClose}>
          Fertig
        </Button>
      }
    >
      {props.webhook.secret ? (
        <SecretReveal label={`Signatur-Geheimnis für „${props.webhook.name}"`} value={props.webhook.secret} testId="webhooks-out-secret" />
      ) : (
        <p className="lfx-muted">Der Server hat kein Geheimnis geliefert.</p>
      )}
      <div className="lfx-stack lfx-wh-explain" data-testid="webhooks-out-signature-help">
        <strong>Signatur prüfen</strong>
        <span className="lfx-muted">Jede Zustellung trägt die Header X-Lagerfeuer-Event, X-Lagerfeuer-Delivery, X-Lagerfeuer-Timestamp und:</span>
        <pre className="lfx-wh-code">{SIGNATURE_EXAMPLE}</pre>
        <span className="lfx-muted">
          Berechne auf deiner Seite HMAC-SHA256 mit dem Geheimnis über <code>timestamp + &quot;.&quot; + Rohbody</code>, vergleiche hex-kodiert in konstanter Zeit
          und verwirf alte Zeitstempel (z. B. älter als 5 Minuten).
        </span>
      </div>
    </Dialog>
  );
}

function OutgoingFormDialog(props: { serverId: string; channels: ChannelDto[]; existing?: OutgoingWebhookDto; onClose: () => void; onCreated: (w: OutgoingWebhookDto) => void }) {
  const qc = useQueryClient();
  const ex = props.existing;
  const [name, setName] = useState(ex?.name ?? '');
  const [url, setUrl] = useState(ex?.url ?? '');
  const [events, setEvents] = useState<OutgoingWebhookEvent[]>(ex?.events ?? ['MESSAGE_CREATE']);
  const [channelId, setChannelId] = useState(ex?.channelId ?? '');
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    gcTime: 0, // Einmal-Geheimnis nicht im Mutation-Cache liegen lassen
    mutationFn: (body: FormBody) =>
      ex
        ? outgoingWebhooksApi.update(ex.id, body)
        : outgoingWebhooksApi.create(props.serverId, { ...body, channelId: body.channelId ?? undefined }),
    onSuccess: (w) => {
      void qc.invalidateQueries({ queryKey: outgoingKey(props.serverId) });
      if (ex) {
        toast({ kind: 'success', title: 'Webhook gespeichert' });
        props.onClose();
      } else props.onCreated(w);
    },
  });

  return (
    <Dialog title={ex ? 'Ausgehenden Webhook bearbeiten' : 'Ausgehenden Webhook anlegen'} testId="webhooks-out-form-dialog" onClose={props.onClose}>
      <form
        className="lfx-stack"
        noValidate
        data-testid="webhooks-out-form"
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          if (!name.trim()) return setFormError('Bitte einen Namen angeben.');
          const urlErr = validateHttpsUrl(url);
          if (urlErr) return setFormError(urlErr);
          if (!events.length) return setFormError('Bitte mindestens ein Ereignis wählen.');
          const ordered = ALL_WEBHOOK_EVENTS.filter((ev) => events.includes(ev));
          const body: FormBody = { name: name.trim(), url: url.trim(), events: ordered };
          if (ex) body.channelId = channelId || null;
          else if (channelId) body.channelId = channelId;
          save.mutate(body);
        }}
      >
        <Field label="Name">
          {(id) => (
            <input
              id={id}
              className="lfx-input"
              data-testid="webhooks-out-name"
              value={name}
              maxLength={FIXED_LIMITS.webhookNameMax}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Ziel-URL" hint="Nur https://. Der Server prüft zusätzlich, dass das Ziel öffentlich erreichbar ist (keine internen Adressen).">
          {(id) => (
            <input
              id={id}
              type="url"
              inputMode="url"
              className="lfx-input"
              data-testid="webhooks-out-url"
              placeholder="https://example.org/hook"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          )}
        </Field>
        <fieldset className="lfx-wh-fieldset" data-testid="webhooks-out-events">
          <legend className="lfx-field__label">Ereignisse</legend>
          {ALL_WEBHOOK_EVENTS.map((ev) => (
            <label key={ev} className="lfx-check">
              <input
                type="checkbox"
                data-testid={`webhooks-out-event-${ev}`}
                checked={events.includes(ev)}
                onChange={(e) => setEvents((cur) => (e.target.checked ? [...cur.filter((x) => x !== ev), ev] : cur.filter((x) => x !== ev)))}
              />
              {WEBHOOK_EVENT_LABELS[ev]} <code className="lfx-muted lfx-mono lfx-wh-small">{ev}</code>
            </label>
          ))}
        </fieldset>
        <Field label="Kanal-Filter (optional)">
          {(id) => (
            <select id={id} className="lfx-select" data-testid="webhooks-out-channel" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
              <option value="">Alle Kanäle</option>
              {props.channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {channelLabel(c)}
                </option>
              ))}
            </select>
          )}
        </Field>
        {formError && (
          <div className="lfx-error" role="alert" data-testid="webhooks-out-form-error">
            {formError}
          </div>
        )}
        {save.isError && (
          <div className="lfx-error" role="alert" data-testid="webhooks-out-save-error">
            {webhooksErrorText(save.error)}
          </div>
        )}
        <div className="lfx-row lfx-wh-actions">
          <Button variant="ghost" testId="webhooks-out-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" testId="webhooks-out-submit" disabled={save.isPending}>
            {ex ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function statusTone(s: OutgoingWebhookDeliveryDto['status']) {
  return s === 'SUCCESS' ? 'ok' : s === 'FAILED' ? 'danger' : 'warn';
}
const STATUS_LABEL: Record<OutgoingWebhookDeliveryDto['status'], string> = { SUCCESS: 'Erfolg', FAILED: 'Fehlgeschlagen', PENDING: 'Ausstehend' };

function Deliveries(props: { webhookId: string }) {
  const q = useInfiniteQuery({
    queryKey: deliveriesKey(props.webhookId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => outgoingWebhooksApi.deliveries(props.webhookId, { before: pageParam, limit: DELIVERY_PAGE }),
    // Seiten sind aufsteigend sortiert → ältester Eintrag steht vorn.
    getNextPageParam: (last) => (last.length < DELIVERY_PAGE ? undefined : last[0]?.id),
  });
  if (q.isPending) return <Loading testId="webhooks-out-deliveries-loading" />;
  if (q.isError)
    return (
      <div className="lfx-error" role="alert" data-testid="webhooks-out-deliveries-error">
        {webhooksErrorText(q.error)}
      </div>
    );
  const rows = q.data.pages.flatMap((p) => [...p].reverse());
  if (!rows.length) return <Empty testId="webhooks-out-deliveries-empty">Noch keine Zustellungen.</Empty>;
  return (
    <div className="lfx-stack">
      <div className="lfx-table-wrap">
        <table className="lfx-table" data-testid="webhooks-out-deliveries">
          <thead>
            <tr>
              <th scope="col">Ereignis</th>
              <th scope="col">Versuch</th>
              <th scope="col">Status</th>
              <th scope="col">HTTP</th>
              <th scope="col">Fehler</th>
              <th scope="col">Zeit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} data-testid="webhooks-out-delivery-row">
                <td className="lfx-mono">{d.event === 'PING' ? 'PING (Test)' : d.event}</td>
                <td>{d.attempt}</td>
                <td>
                  <Badge tone={statusTone(d.status)}>{STATUS_LABEL[d.status]}</Badge>
                </td>
                <td>{d.responseStatus ?? '–'}</td>
                <td className="lfx-wh-err-cell">{d.error ?? '–'}</td>
                <td>{formatDateTime(d.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {q.hasNextPage && (
        <div>
          <Button testId="webhooks-out-deliveries-more" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {q.isFetchingNextPage ? 'Lädt …' : 'Ältere laden'}
          </Button>
        </div>
      )}
    </div>
  );
}

function OutgoingRow(props: { webhook: OutgoingWebhookDto; serverId: string; channels: ChannelDto[]; onSecret: (w: OutgoingWebhookDto, title: string) => void; onEdit: () => void }) {
  const qc = useQueryClient();
  const w = props.webhook;
  const [error, setError] = useState<string | null>(null);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [testResult, setTestResult] = useState<OutgoingWebhookDeliveryDto | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: outgoingKey(props.serverId) });
  const onError = (err: unknown) => setError(webhooksErrorText(err));

  const toggle = useMutation({
    mutationFn: (active: boolean) => outgoingWebhooksApi.update(w.id, { active }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError,
  });
  const regen = useMutation({
    gcTime: 0, // Einmal-Geheimnis nicht im Mutation-Cache liegen lassen
    mutationFn: () => outgoingWebhooksApi.regenerateSecret(w.id),
    onSuccess: (res) => {
      setError(null);
      props.onSecret(res, 'Neues Signatur-Geheimnis');
    },
    onError,
  });
  const test = useMutation({
    mutationFn: () => outgoingWebhooksApi.test(w.id),
    onSuccess: (d) => {
      setError(null);
      setTestResult(d);
      refresh();
      void qc.invalidateQueries({ queryKey: deliveriesKey(w.id) });
    },
    onError,
  });
  const remove = useMutation({ mutationFn: () => outgoingWebhooksApi.remove(w.id), onSuccess: refresh, onError });

  const failedOff = !w.active && w.failureCount > 0;
  const channel = w.channelId ? props.channels.find((c) => c.id === w.channelId) : undefined;
  return (
    <li className={`lfx-card lfx-stack lfx-wh-item${failedOff ? ' lfx-wh-item--failed' : ''}`} data-testid="webhooks-out-row" data-webhook-id={w.id}>
      <div className="lfx-row">
        <div className="lfx-grow">
          <div className="lfx-wh-name" data-testid="webhooks-out-name-text">
            {w.name}{' '}
            {failedOff ? (
              <Badge tone="danger" testId="webhooks-out-failed-badge">
                Nach Fehlern deaktiviert
              </Badge>
            ) : !w.active ? (
              <Badge testId="webhooks-out-inactive-badge">inaktiv</Badge>
            ) : null}
          </div>
          <div className="lfx-muted lfx-mono lfx-wh-url" data-testid="webhooks-out-url-text">
            {w.url}
          </div>
        </div>
        <label className="lfx-check lfx-wh-switch">
          <input
            type="checkbox"
            role="switch"
            aria-checked={w.active}
            data-testid={`webhooks-out-active-${w.id}`}
            checked={w.active}
            disabled={toggle.isPending}
            onChange={(e) => toggle.mutate(e.target.checked)}
          />
          aktiv
        </label>
      </div>
      <div className="lfx-row" aria-label="Ereignisse">
        {w.events.map((ev) => (
          <Badge key={ev} tone="accent" title={ev} testId="webhooks-out-event-badge">
            {WEBHOOK_EVENT_LABELS[ev] ?? ev}
          </Badge>
        ))}
        {w.channelId && <Badge testId="webhooks-out-channel-badge">nur {channelLabel(channel)}</Badge>}
      </div>
      <div className="lfx-wh-meta lfx-muted" data-testid="webhooks-out-status">
        <span>Letzte Zustellung: {formatDateTime(w.lastDeliveryAt)}</span>
        <span>HTTP: {w.lastStatus ?? '–'}</span>
        <span className={w.failureCount > 0 ? 'lfx-wh-bad' : undefined}>Fehler in Folge: {w.failureCount}</span>
        {w.lastError && <span className="lfx-wh-bad">Letzter Fehler: {w.lastError}</span>}
      </div>
      <div className="lfx-row">
        <Button testId={`webhooks-out-edit-${w.id}`} onClick={props.onEdit}>
          Bearbeiten
        </Button>
        <Button testId={`webhooks-out-test-${w.id}`} disabled={test.isPending} onClick={() => test.mutate()}>
          {test.isPending ? 'Sende …' : 'Test senden'}
        </Button>
        <Button
          variant="ghost"
          testId={`webhooks-out-deliveries-toggle-${w.id}`}
          aria-expanded={showDeliveries}
          onClick={() => setShowDeliveries((v) => !v)}
        >
          {showDeliveries ? 'Zustellungen ausblenden' : 'Zustellungen anzeigen'}
        </Button>
        <ConfirmButton label="Secret neu erzeugen" confirmLabel="Altes Secret ungültig machen?" testId={`webhooks-out-regen-${w.id}`} onConfirm={() => regen.mutate()} />
        <ConfirmButton label="Löschen" confirmLabel="Wirklich löschen?" testId={`webhooks-out-delete-${w.id}`} onConfirm={() => remove.mutate()} />
      </div>
      {testResult && (
        <div className="lfx-row lfx-wh-test" role="status" data-testid={`webhooks-out-test-result-${w.id}`}>
          Test ({testResult.event}): <Badge tone={statusTone(testResult.status)}>{STATUS_LABEL[testResult.status]}</Badge>
          {testResult.responseStatus !== null && <span>HTTP {testResult.responseStatus}</span>}
          {testResult.error && <span className="lfx-wh-bad">{testResult.error}</span>}
        </div>
      )}
      {error && (
        <div className="lfx-error" role="alert" data-testid={`webhooks-out-error-${w.id}`}>
          {error}
        </div>
      )}
      {showDeliveries && <Deliveries webhookId={w.id} />}
    </li>
  );
}

export function OutgoingWebhooks(props: { serverId: string; channels: ChannelDto[] }) {
  const [form, setForm] = useState<{ existing?: OutgoingWebhookDto } | null>(null);
  const [secret, setSecret] = useState<{ webhook: OutgoingWebhookDto; title: string } | null>(null);
  const q = useQuery({ queryKey: outgoingKey(props.serverId), queryFn: () => outgoingWebhooksApi.list(props.serverId) });
  return (
    <section className="lfx-stack" data-testid="webhooks-out" aria-label="Ausgehende Webhooks">
      <div className="lfx-row lfx-row--between">
        <p className="lfx-muted lfx-wh-note">Ausgehende Webhooks melden Ereignisse dieses Servers per signiertem HTTPS-POST an deine Dienste.</p>
        <Button variant="primary" testId="webhooks-out-create" disabled={q.isError} onClick={() => setForm({})}>
          Webhook anlegen
        </Button>
      </div>
      {q.isPending ? (
        <Loading testId="webhooks-out-loading" />
      ) : q.isError ? (
        <div className="lfx-error" role="alert" data-testid="webhooks-out-load-error">
          {webhooksErrorText(q.error)}
        </div>
      ) : q.data.length === 0 ? (
        <Empty testId="webhooks-out-empty">Noch keine ausgehenden Webhooks.</Empty>
      ) : (
        <ul className="lfx-wh-list" data-testid="webhooks-out-list">
          {q.data.map((w) => (
            <OutgoingRow
              key={w.id}
              webhook={w}
              serverId={props.serverId}
              channels={props.channels}
              onEdit={() => setForm({ existing: w })}
              onSecret={(webhook, title) => setSecret({ webhook, title })}
            />
          ))}
        </ul>
      )}
      {form && (
        <OutgoingFormDialog
          serverId={props.serverId}
          channels={props.channels}
          existing={form.existing}
          onClose={() => setForm(null)}
          onCreated={(w) => {
            setForm(null);
            setSecret({ webhook: w, title: 'Ausgehender Webhook angelegt' });
          }}
        />
      )}
      {secret && <SecretDialog webhook={secret.webhook} title={secret.title} onClose={() => setSecret(null)} />}
    </section>
  );
}
